import React, { useState, useRef, useEffect } from "react";
import { Button, Form, message, Tooltip } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import "./style.css";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import InputCard from "../../../../Components/Cards/InputCard";
import { useLocation, useNavigate } from "react-router-dom";
import ModuleSelctorCards from "../../../../Components/Cards/ModuleSelectorCards";
import FuelIcon from "../../../../Assets/Icons/fuel-icon.svg";
import ClipboardIcon from "../../../../Assets/Icons/clipboard.svg";
import CarPartsIcon from "../../../../Assets/Icons/car_repair.svg";
import {
  getFleetManagerById,
  updateFleetManager,
} from "../../../../API/Admin/FleetManagement";
import { getModules, getDocumentCategories } from "../../../../API/Config";
import { navixyApiKeyFieldProps } from "../../../../Utlis/NavixyApiKeys";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-US");
};

const MODULE_ICONS_BY_CODE = {
  fuel: FuelIcon,
  maintenance: ClipboardIcon,
  parts: CarPartsIcon,
};

const PRICING_TABS = ["Monthly Pricing", "Annually Pricing"];

const buildModuleOption = (mod, isAnnual) => {
  const fallbackIcon =
    MODULE_ICONS_BY_CODE[mod.code?.toLowerCase()] || ClipboardIcon;
  const iconSrc = mod.icon ? resolveAvatarUrl(mod.icon) : fallbackIcon;
  const rawPrice = isAnnual
    ? mod.annual_price_per_vehicle
    : mod.monthly_price_per_vehicle;
  const price = parseFloat(rawPrice);
  const isInactive = mod.status ? mod.status !== "active" : false;

  return {
    id: mod.id,
    title: mod.name,
    description: mod.description,
    price: rawPrice ? (price ? `${mod.currency || "JMD"} ${price.toFixed(2)}/vehicle` : "Free") : undefined,
    icon: <img src={iconSrc} alt={mod.name} />,
    disabled: isInactive,
    tag: isInactive ? "Coming Soon" : undefined,
  };
};

const mapDocument = (doc, idx) => ({
  key: String(doc.id ?? idx),
  id: doc.id,
  name: doc.file ? doc.file.split("/").pop() : "-",
  category: doc.category || "-",
  allowedFormats: doc.allowed_formats || [],
  date: formatDate(doc.uploaded_at),
  expiryDate: formatDate(doc.expiry_date),
  fileUrl: resolveAvatarUrl(doc.file),
});

const MAX_FILENAME_LENGTH = 60;

const shortenFileName = (file, maxLength = MAX_FILENAME_LENGTH) => {
  if (file.name.length <= maxLength) return file;

  const dotIndex = file.name.lastIndexOf(".");
  const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : "";
  const base = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
  const truncatedBase = base.slice(0, Math.max(1, maxLength - ext.length));

  return new File([file], `${truncatedBase}${ext}`, { type: file.type });
};

const EditCompanyOnboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const managerId = location.state?.manager?.key;
  const [form] = Form.useForm();
  const [manager, setManager] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [catalogModules, setCatalogModules] = useState([]);
  const [pricingPeriod, setPricingPeriod] = useState(PRICING_TABS[0]);
  const [documents, setDocuments] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const fileInputRef = useRef(null);

  const applyManagerData = (data) => {
    form.setFieldsValue({
      legalBusinessName: data.legal_name,
      registrationNumber: data.registration_number,
      contactNumber: data.contact_phone,
      currency: data.currency || "JMD",
      headQuarterAddress: data.address,
      apiKeys: data.navixy_api_keys || [],
      primarycontactperson: data.primary_contact_person,
      designation: data.designation,
      businessemail: data.contact_email,
      phonenumber: data.contact_phone,
      secondarycontactperson: data.secondary_contact_person,
      secondaryDesignation: data.secondary_designation,
      secondaryEmail: data.secondary_contact_email,
      secondaryPhone: data.secondary_contact_phone,
    });
    setAvatar(resolveAvatarUrl(data.avatar));
    setAvatarFile(null);

    const assigned = Array.isArray(data.assigned_modules)
      ? data.assigned_modules
      : [];
    setSelectedModule(
      assigned.length ? { id: assigned[0].id, title: assigned[0].name } : null,
    );
    setDocuments((data.documents || []).map(mapDocument));
  };

  useEffect(() => {
    if (!managerId) return;

    const fetchManager = async () => {
      try {
        const res = await getFleetManagerById(managerId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          setManager(data);
          applyManagerData(data);
        } else {
          message.error(res?.data?.message || "Failed to load fleet manager.");
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load fleet manager.",
        );
      }
    };

    fetchManager();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerId]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await getModules();
        const modules = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        setCatalogModules(modules);
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load modules.",
        );
      }
    };

    fetchModules();
  }, []);

  useEffect(() => {
    const fetchDocumentCategories = async () => {
      const res = await getDocumentCategories("fleet-manager");
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setDocumentCategories(
          list.map((cat) => ({
            label: cat.document_name,
            value: cat.document_name,
            allowedFormats: cat.allowed_formats,
          })),
        );
      }
    };

    fetchDocumentCategories();
  }, []);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(shortenFileName(file));

      const reader = new FileReader();
      reader.onload = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    if (manager) applyManagerData(manager);
  };

  // FIRST INPUT CARD FIELDS
  const registrationInformation = [
    {
      name: "legalBusinessName",
      label: "Legal Business Name",
      fullWidth: false,
      type: "text",
      placeholder: "Enter Legal Business Name",
      rules: [{ required: true, message: "Business name is required" }],
    },
    {
      name: "registrationNumber",
      label: "Registration Number",
      placeholder: "Enter Registration Number",
      fullWidth: false,
      type: "text",
      rules: [{ required: true, message: "Registration number is required" }],
    },
    {
      name: "contactNumber",
      label: "Company Contact Number",
      placeholder: "Enter Contact Number",
      fullWidth: false,
      type: "phone",
      rules: [
        { required: true, message: "Contact number is required" },
        {
          validator: (_, value) => {
            if (!value || value.length >= 10) {
              return Promise.resolve();
            }
            return Promise.reject("Enter valid phone number");
          },
        },
      ],
    },
    {
      name: "currency",
      label: "Currency",
      placeholder: "Enter amount",
      fullWidth: false,
      type: "currency",
      rules: [{ required: true, message: "Currency is required" }],
    },
    {
      name: "headQuarterAddress",
      label: "Headquarters Address",
      placeholder: "Enter Address Of Headquarters",
      fullWidth: true,
      type: "textarea",
      rules: [{ required: true, message: "Address is required" }],
    },
    {
      name: "apiKeys",
      label: "API Key",
      placeholder: "Enter API key",
      type: "multi-input",
      addLabel: "Add another API key",
      ...navixyApiKeyFieldProps,
      fullWidth: true,
    },
  ];


  const contactInfoFields = [
    {
      name: "primarycontactperson",
      label: "Primary Contact Person",
      placeholder: "Enter Name Of Primary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      name: "designation",
      label: "Designation",
      placeholder: "Enter Designation Of Primary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Designation is required" }],
    },
    {
      name: "businessemail",
      label: "Business Email",
      placeholder: "Enter Email Of Primary Contact Person",
      type: "text",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter valid email" },
      ],
    },
    {
      name: "phonenumber",
      label: "Phone Number",
      placeholder: "Enter Contact Number Of Primary Contact Person",
      type: "phone",
      rules: [
        { required: true, message: "Phone number is required" },
        {
          validator: (_, value) => {
            if (!value || value.length >= 10) {
              return Promise.resolve();
            }
            return Promise.reject("Enter valid phone number");
          },
        },
      ],
    },
  ];

  // THIRD INPUT CARD FIELDS
  const secondaryContactInformationFields = [
    {
      name: "secondarycontactperson",
      label: "Secondary Contact Person",
      placeholder: "Enter Name of Secondary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      name: "secondaryDesignation",
      label: "Designation",
      placeholder: "Enter Designation of Secondary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Designation is required" }],
    },
    {
      name: "secondaryEmail",
      label: "Business Email",
      placeholder: "Enter Buisness Email Of Secondary Contact Person",
      type: "text",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter valid email" },
      ],
    },
    {
      name: "secondaryPhone",
      label: "Phone Number",
      placeholder: "Enter Contact Number of Secondary Contact Person",
      type: "phone",
      rules: [{ required: true, message: "Phone is required" }],
    },
  ];

  const isAnnual = pricingPeriod === "Annually Pricing";
  const moduleOptions = catalogModules.map((mod) =>
    buildModuleOption(mod, isAnnual),
  );

  const handleSubmit = async () => {
    if (!managerId) {
      message.error("Missing fleet manager id.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error("Please check the form for missing fields.");
      return;
    }

    const formData = new FormData();
    formData.append("legal_name", values.legalBusinessName);
    formData.append("registration_number", values.registrationNumber);
    formData.append("contact_phone", values.contactNumber);
    formData.append("currency", values.currency);
    formData.append("address", values.headQuarterAddress);
    formData.append("navixy_api_keys", JSON.stringify(values.apiKeys || []));
    formData.append("primary_contact_person", values.primarycontactperson);
    formData.append("designation", values.designation);
    formData.append("contact_email", values.businessemail);
    formData.append("secondary_contact_person", values.secondarycontactperson);
    formData.append("secondary_designation", values.secondaryDesignation);
    formData.append("secondary_contact_email", values.secondaryEmail);
    formData.append("secondary_contact_phone", values.secondaryPhone);

    if (selectedModule) {
      formData.append("assigned_module_ids", selectedModule.id);
    }

    if (avatarFile) {
      formData.append("avatar", avatarFile);
    }

    const newDocuments = documents.filter((doc) => doc.file);
    newDocuments.forEach((doc) => {
      formData.append("documents", doc.file);
    });
    if (newDocuments.length > 0) {
      formData.append(
        "document_categories",
        JSON.stringify(newDocuments.map((doc) => doc.category)),
      );
      formData.append(
        "document_expiry_dates",
        JSON.stringify(newDocuments.map((doc) => doc.expiryDate || null)),
      );
    }

    try {
      const res = await updateFleetManager(managerId, formData);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(res?.data?.message || "Onboarding data updated!");
        navigate("/fleet-management-detail", {
          state: { record: { key: managerId } },
          replace: true,
        });
      } else {
        message.error(res?.data?.message || "Failed to update company.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to update company.",
      );
    }
  };

  return (
    <div className="edit-company">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
      <Form form={form} layout="vertical">
        <div className="row">
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={handleButtonClick}
                >
                  Upload Photo
                </Button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <div className="profile-avatar">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                    }}
                  />
                ) : (
                  "T"
                )}
              </div>
            </div>
            <div className="profile-body"></div>
          </div>
        </div>
        <div>
          <div>
            <InputCard
              title="Registration Information"
              fields={registrationInformation}
            />
          </div>
          <div>
            <InputCard title="Contact Information" fields={contactInfoFields} />
          </div>
          <div>
            <InputCard
              title="Secondary Contact Information"
              fields={secondaryContactInformationFields}
            />
          </div>
          <div className="report-navigation">
            <div className="nav-tabs">
              {PRICING_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`nav-item ${pricingPeriod === tab ? "active" : ""}`}
                  onClick={() => setPricingPeriod(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Tooltip title="Modules can't be edited here. Changes should purely be initiated by the fleet manager company when they update their subscription package.">
              <div>
                <ModuleSelctorCards
                  options={moduleOptions}
                  onChange={setSelectedModule}
                  selectedId={selectedModule?.id}
                  footerLabel={isAnnual ? "Annual Rate" : "Monthly Rate"}
                  showFooter={true}
                  allowSelection={false}
                />
              </div>
            </Tooltip>
          </div>

          <div>
            <GenericDocumentManagerTable
              initialDocuments={documents}
              setDocuments={setDocuments}
              hideDateColumn={true}
              documentTypes={documentCategories}
            />
          </div>
        </div>
        <div className="btns-row">
          <Button children="Cancel" onClick={handleCancel} />
          <Button
            children="Save Changes"
            onClick={() => handleSubmit()}
            className="save-btn"
          />
        </div>
      </Form>
    </div>
  );
};

export default EditCompanyOnboarding;
